import React, { forwardRef } from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import RouteService from '../../../services/route-service';

const StyledAnchor = styled.a`
	&:hover {
		text-decoration: none;
	}
`;

function isModifiedEvent(event) {
	return !!(event.metaKey || event.altKey || event.ctrlKey || event.shiftKey);
}

const LinkAnchor = forwardRef(
	(
		{
			navigate,
			onClick,
			...rest
		},
		forwardedRef
	) => {
		const { target } = rest;

		const props = {
			...rest,
			onClick: (event) => {
				try {
					if( onClick ) {
						onClick(event);
					}
				}
				catch(ex) {
					event.preventDefault();
					throw ex;
				}

				if(
					!event.defaultPrevented // onClick prevented default
          && event.button === 0 // ignore everything but left clicks
          && (!target || target === '_self') // let browser handle "target=_blank" etc.
          && !isModifiedEvent(event) // ignore clicks with modifier keys
				) {
					event.preventDefault();
					navigate();
				}
			},
			ref: forwardedRef
		};

		return <StyledAnchor {...props} />;
	}
);

LinkAnchor.propTypes = {
	navigate: PropTypes.func,
	onClick: PropTypes.func
};
LinkAnchor.displayName = 'LinkAnchor';

const Link = forwardRef(
	(
		{
			component = LinkAnchor,
			to,
			disable = false,
			...rest
		},
		forwardedRef
	) => {
		// Defensive: handle undefined or malformed `to` objects gracefully in Storybook
		const { state, params } = to || {};
		const safeState = typeof state === 'string' ? state : undefined;
		const href = !disable && safeState ? RouteService.stateHref(safeState, params) : null;

		const props = {
			...rest,
			href,
			navigate() {
				if(!disable && safeState) {
					RouteService.navigateTo(safeState, params);
				}
			},
			ref: forwardedRef
		};

		return React.createElement(component, props);
	}
);

Link.propTypes = {
	to: PropTypes.shape({
		state: PropTypes.string.isRequired,
		params: PropTypes.object
	}),
	disable: PropTypes.bool,
	rest: PropTypes.object,
	navigate: PropTypes.func,
	component: PropTypes.func,
	onClick: PropTypes.func
};

Link.displayName = 'LinkAnchor';

export default Link;
